export const IRISH_FUNDS_CONTEXT = {
  name: "Irish Funds",
  established: 1991,
  description: "The industry association for the international investment funds community in Ireland",
  mission: "To promote Ireland as the leading location for international fund services and support our members through advocacy, education, and networking",
  
  services: {
    memberServices: [
      "Industry advocacy and representation",
      "Regulatory guidance and support", 
      "Working groups and committees",
      "Educational events and training",
      "Networking opportunities",
      "Policy development and consultation"
    ],
    
    keyAreas: [
      "Fund domiciliation and administration",
      "Asset management services",
      "UCITS and AIFM regulations",
      "ETF market development",
      "ESG and sustainable finance",
      "Cross-border distribution",
      "Regulatory compliance",
      "Tax and legal frameworks"
    ]
  },
  
  regulations: {
    eu: ["UCITS", "AIFMD", "SFDR", "EU Taxonomy", "MiFID II", "CSDR"],
    irish: ["Central Bank regulations", "Irish Fund Law", "Tax legislation"],
    global: ["FATCA", "CRS", "OECD guidelines"]
  },
  
  memberTypes: [
    "Fund management companies",
    "Investment advisors", 
    "Fund administrators",
    "Custodians and depositaries",
    "Transfer agents",
    "Legal and professional services",
    "Technology providers"
  ]
};

// Nano AI Topic Filter Configuration (Backend-based)
export const NANO_TOPIC_FILTER = {
  endpoint: "/daire/topic-filter", // Your backend endpoint
  method: "POST",
  
  fallbackResponse: {
    allowed: false, // SAFER: Block queries when filter fails
    confidence: "fallback",
    reason: "Filter unavailable - redirecting for safety"
  }
};  

// Redirect Messages for Off-Topic Queries
export const REDIRECT_MESSAGES = {
    standard: `I'm sorry, but I cannot help with that. I'm specifically designed to assist with Irish Funds and investment management topics.

How can I help you with your fund services or regulatory questions today?`,

  casual: `Hi there! I'm Daire, focused on helping with Irish Funds and investment management topics. 

What would you like to know about the fund services industry? 🚀`,

  educational: `I'm here to help with Irish Funds industry topics! 📚

Whether you need information about regulations, fund structures, or industry trends, I'm ready to assist with your professional queries.

What can I help you explore today?`
};

// AI System Configuration
export const DAIRE_AI_CONFIG = {
  // Main system prompt for Daire
  systemPrompt: `You are Daire, a sophisticated AI assistant for Irish Funds members. You provide expert guidance on:

CORE EXPERTISE:
- Irish Funds organization and member services
- Investment fund regulations (UCITS, AIFMD, SFDR, MiFID II)
- ETF and fund market analysis
- ESG and sustainable finance
- Regulatory compliance and guidance
- Working groups and industry committees
- Professional development and networking

PERSONALITY:
- Professional yet approachable
- Knowledgeable about complex financial regulations
- Supportive of member needs and queries
- Proactive in providing relevant context
- Clear communicator of technical concepts

RESPONSE STYLE:
- Use member's first name when appropriate
- Provide specific, actionable information
- Include relevant regulatory context
- Offer to elaborate on complex topics
- Maintain professional Irish Funds tone

Your role is to be the intelligent interface between Irish Funds members and the organization's knowledge base, helping them navigate the complex world of international fund services.`,

  // Topic classification prompts
  intentClassification: {
    searchIntent: "User is seeking specific information or documents",
    casualIntent: "User is making general conversation or asking basic questions", 
    educationalIntent: "User wants to learn about concepts or industry topics",
    regulatoryIntent: "User needs guidance on compliance or regulatory matters"
  },

  // Response formatting guidelines
  formatting: {
    useMarkdown: true,
    includeEmojis: "sparingly for professional context",
    maxResponseLength: 800,
    includeSourceAttribution: true
  }
};

// Topic Filter Implementation - NANO AI (CONSERVATIVE)
export const executeTopicFilter = async (query) => {
  try {
    // Import apiHelper dynamically to avoid circular imports
    const { default: apiHelper } = await import('../utils/apiHelper');
    
    // Get auth token and make API call
    const authToken = await apiHelper.getAuthToken();
    
    const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}daire/topic-filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        query: query
      })
    });
    
    if (!fetchResponse.ok) {
      throw new Error(`HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
    }
    
    const response = await fetchResponse.json();
    
    return {
      allowed: response.allowed,
      confidence: "high",
      method: "conservative_nano_ai",
      fallback: response.fallback || false
    };

  } catch (error) {
    // Fallback: allow queries when filter fails (safer for business)
    return {
      allowed: true,
      confidence: "fallback",
      method: "filter_failed"
    };
  }
};

// Helper function to get appropriate redirect message
export const getRedirectMessage = (context = 'standard') => {
  return REDIRECT_MESSAGES[context] || REDIRECT_MESSAGES.standard;
};

// Validate environment setup
export const validateDaireConfig = () => {
  const checks = {
    apiUrl: !!process.env.REACT_APP_API_URL,
    backendAvailable: true
  };
  
  return checks;
};

// Export everything needed for DaireChat integration
export default {
  IRISH_FUNDS_CONTEXT,
  NANO_TOPIC_FILTER,
  REDIRECT_MESSAGES,
  DAIRE_AI_CONFIG,
  executeTopicFilter,
  getRedirectMessage,
  validateDaireConfig
}; 